import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal, Signal} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {HintComponent} from "@common/components/hint/hint.component"
import {PlaceholderComponent} from "@common/components/placeholders/placeholder/placeholder.component"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {RefreshService} from "@common/services/refresh/refresh.service"
import {ContentTypeModel} from "@generated"
import {
    MaterialOutputPublishingButtonComponent,
    MaterialOutputPublishingButtonProps,
} from "@platform/components/materials/material-output-publishing-button/material-output-publishing-button.component"
import {ConfiguraPublishStatus, MaterialOutputsPublishingService} from "@platform/services/material/material-outputs-publishing.service"

/**
 * The "Outputs publishing" section of the material sidebar: where this material stands with each
 * external content platform, and the action that moves it forward. Configura's Material Database is the
 * only target today; pCon joins as a second button in this same section rather than a section of its
 * own.
 *
 * Thin by design. Every question about *what* the state is belongs to
 * `MaterialOutputsPublishingService`, so the material list (step 18) can ask the same questions
 * without this component. What lives here is the part that only a view can answer — which icon, which
 * tooltip, what happens on click — plus the per-material state and the polling subscription, which
 * need a lifecycle and therefore cannot sit on a root singleton.
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-material-outputs-publishing",
    imports: [HintComponent, MaterialOutputPublishingButtonComponent, PlaceholderComponent],
    templateUrl: "./material-outputs-publishing.component.html",
    styleUrl: "./material-outputs-publishing.component.scss",
})
export class MaterialOutputsPublishingComponent implements OnInit {
    readonly $materialId = input.required<string>({alias: "materialId"})
    /** The material, only so permissions can be checked against its organization. */
    readonly $material = input.required<{organization?: {id: string} | null} | null>({alias: "material"})

    readonly $status = signal<ConfiguraPublishStatus | undefined>(undefined)
    /** True between the click and the reload, so the button cannot be pressed twice. */
    readonly $submitting = signal(false)

    private readonly destroyRef = inject(DestroyRef)
    readonly notifications = inject(NotificationsService)
    readonly permission = inject(PermissionsService)
    readonly publishing = inject(MaterialOutputsPublishingService)
    readonly refresh = inject(RefreshService)

    readonly $can = this.permission.$to

    /**
     * Publishing is day-to-day work for whoever maintains a material, so it follows the same gate as
     * creating outputs. The mutation is the real authority — it checks `create` on `Job` and on the
     * binding, neither of which the frontend ability layer exposes — so this can only ever be
     * narrower, which is the safe direction: it may hide a button someone could have used, never the
     * reverse.
     */
    readonly $canPublish = computed(() => this.$can().update.material(this.$material(), "outputs"))

    ngOnInit() {
        void this.loadStatus()
        this.refresh
            .observeItem$({id: this.$materialId(), __typename: ContentTypeModel.Material}, false)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => void this.loadStatus())
    }

    async loadStatus() {
        const status = await this.publishing.getConfiguraPublishStatus(this.$materialId())
        this.$status.set(status)
        this.$submitting.set(false)

        const jobId = status.binding?.latestJobTask?.id
        if (status.action.kind === "inFlight" && jobId) {
            this.watchJob(jobId)
        }
    }

    private watchedJobId: string | null = null

    /**
     * The service hands out a cold observable, so the timer is ours and dies with the component. It
     * completes on the job's terminal state; the refresh broadcast then re-resolves the publish state
     * (through the subscription in `ngOnInit`), which is what turns the spinner into a check mark or
     * a retry.
     */
    private watchJob(jobId: string) {
        if (this.watchedJobId === jobId) {
            return
        }
        this.watchedJobId = jobId
        this.publishing
            .watchPublishJobUntilSettled$(jobId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                complete: () => {
                    this.watchedJobId = null
                    // Refreshes the material as a whole, so the outputs section above reacts too.
                    this.refresh.item({id: this.$materialId(), __typename: ContentTypeModel.Material})
                },
                error: (error: unknown) => {
                    this.watchedJobId = null
                    this.notifications.showError(error)
                },
            })
    }

    async publishToConfigura() {
        this.$submitting.set(true)
        try {
            const binding = await this.publishing.triggerConfiguraMaterialDatabasePublish({
                materialId: this.$materialId(),
                showNotification: true,
            })
            // The mutation returns the job it just created, so the spinner starts now rather than one
            // round trip later.
            if (binding.latestJobTask) {
                this.watchJob(binding.latestJobTask.id)
            }
            await this.loadStatus()
        } catch (error) {
            this.$submitting.set(false)
            // The backend messages are the actionable content here — no Material Database profile, a sku matching
            // zero or several textiles, outputs behind the material — so they are shown verbatim, and
            // for longer than the 5s default because they ask the reader to go and do something.
            this.notifications.showError(error, 10_000, "Dismiss")
        }
    }

    readonly $configuraButtonProps: Signal<MaterialOutputPublishingButtonProps | undefined> = computed(() => {
        const status = this.$status()
        if (!status) {
            return undefined
        }
        const shared = {label: "Configura", icon: "fa-solid fa-c"} as const
        const publishedAt = status.binding?.lastPublishedAt

        if (this.$submitting()) {
            return {...shared, state: "inFlight", tooltip: "Submitting…"}
        }
        // The click regenerates the Configura export before publishing, which is minutes of rendering
        // rather than an upload. Said up front, because the spinner alone does not explain the wait.
        const exportFirst = " The Configura export will be generated first, so this takes a few minutes."
        switch (status.action.kind) {
            case "publish":
                return {
                    ...shared,
                    state: "publish",
                    tooltip: "Publish to the Configura Material Database." + (status.action.generatesExport ? exportFirst : ""),
                    onTrigger: () => void this.publishToConfigura(),
                }
            case "republish":
                return {
                    ...shared,
                    state: "republish",
                    tooltip:
                        (publishedAt
                            ? `The Material Database holds an older revision, published ${new Date(publishedAt).toLocaleString()} — publish again.`
                            : "The Material Database holds an older revision — publish again.") + (status.action.generatesExport ? exportFirst : ""),
                    onTrigger: () => void this.publishToConfigura(),
                }
            case "retry":
                return {
                    ...shared,
                    state: "retry",
                    tooltip: "The last publish failed — try again." + (status.action.generatesExport ? exportFirst : ""),
                    onTrigger: () => void this.publishToConfigura(),
                }
            case "inFlight":
                return {...shared, state: "inFlight", tooltip: status.action.scheduled ? "Publish scheduled" : "Publishing…"}
            case "upToDate":
                return {
                    ...shared,
                    state: "upToDate",
                    tooltip: publishedAt
                        ? `The Material Database holds the latest revision, published ${new Date(publishedAt).toLocaleString()}`
                        : "The Material Database holds the latest revision",
                }
            case "blocked":
                return {...shared, state: "blocked", tooltip: status.action.message}
        }
    })
}
